/**
 * WebView session persistence — survives native app process kills.
 *
 * When the OS kills the WebView (memory/battery), React reloads from scratch.
 * This module saves lightweight UI state to localStorage (keyed by sessionId)
 * so users resume at their last step instead of Terms & Conditions.
 *
 * Also deduplicates SESSION_START Sentry logs and reports storage reliability.
 * See docs/session-persistence.md for full design.
 */
import type { ResultStatus } from 'types/imageRecognition.d'
import type { ResultActionType } from 'types/resultActions'

const SESSION_KEY_PREFIX = 'bmt_session_'
/** Legacy separate timer keys from pre-BMT-332 design (step1Timer_*, step2Timer_*) */
const LEGACY_TIMER_KEY_PATTERN = /^step\d+Timer_/
const PERSISTENCE_MARKER_KEY = 'bmt_storage_persistence_marker'
const SESSION_ID_ERROR_SENTINEL = 'SessionIdError'
/** Stale sessions auto-expire after 30 min — prevents restoring abandoned tests */
const SESSION_TTL_MS = 30 * 60 * 1000

export type StorageStatus = 'available' | 'unverified' | 'disabled'

export interface PersistedSessionState {
  currentStep: number
  result: ResultStatus | null
  resultAction: ResultActionType | undefined
  notSureCount: number
  sessionStartLogged: boolean
  testType: string
  timestamp: number
  /** Nested/inner step per stable parent step id (e.g. { step1: 2 }) — not wizard index */
  innerSteps?: Record<string, number>
  /** Timer startedAt epoch ms per timer id (e.g. { step1Timer: 1718000000000 }) */
  timerStarts?: Record<string, number>
  /** S3 folder from eager upload; reused for metadata.json updates after refresh */
  uploadPath?: string
  /** S3 download URLs from eager upload (URLs only, never base64) — for post-refresh analytics */
  imageDownloadUrls?: Array<string>
}

const buildSessionKey = (sessionId: string): string =>
  `${SESSION_KEY_PREFIX}${sessionId}`

const isSessionKey = (key: string): boolean =>
  key.startsWith(SESSION_KEY_PREFIX)

const isLegacyTimerKey = (key: string): boolean =>
  LEGACY_TIMER_KEY_PATTERN.test(key)

const tryLocalStorage = (): boolean => {
  try {
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, '1')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/** Rejects SessionIdError sentinel and empty ids — used before any localStorage write */
export const isValidSessionId = (sessionId: string): boolean =>
  Boolean(sessionId) && sessionId !== SESSION_ID_ERROR_SENTINEL

/** Saves current step, result, and related UI state. Preserves sessionStartLogged flag. */
export const persistSessionState = (
  sessionId: string,
  state: Omit<PersistedSessionState, 'timestamp' | 'sessionStartLogged'>
): void => {
  const storageOk = tryLocalStorage()
  const validId = isValidSessionId(sessionId)

  if (!validId || !storageOk) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)
    const payload: PersistedSessionState = {
      ...state,
      sessionStartLogged: existing?.sessionStartLogged ?? false,
      // Preserve nested steps when MainPage persist omits them
      innerSteps: state.innerSteps ?? existing?.innerSteps ?? {},
      // Preserve timer start times when MainPage persist omits them
      timerStarts: state.timerStarts ?? existing?.timerStarts ?? {},
      // Preserve uploadPath when MainPage persist omits it
      uploadPath: state.uploadPath ?? existing?.uploadPath,
      // Preserve image download URLs when MainPage persist omits them
      imageDownloadUrls: state.imageDownloadUrls ?? existing?.imageDownloadUrls,
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to persist state:', error)
  }
}

/** Reads persisted state; returns null if missing, expired, or localStorage unavailable */
export const restoreSessionState = (
  sessionId: string
): PersistedSessionState | null => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return null
  }

  try {
    const data = readRawSession(sessionId)
    if (!data) {
      return null
    }

    if (Date.now() - data.timestamp > SESSION_TTL_MS) {
      clearSessionState(sessionId)
      return null
    }

    return data
  } catch (error) {
    console.warn('[sessionPersistence] Failed to restore state:', error)
    return null
  }
}

/** Step 6 with DONE — test finished; refresh should start a new run from step 0 */
export const isCompletedSessionState = (
  state: Pick<PersistedSessionState, 'currentStep' | 'resultAction'>
): boolean => state.currentStep === 6 && state.resultAction === 'DONE'

/** Marks SESSION_START as logged so WebView reload does not duplicate the Sentry event */
export const markSessionStartLogged = (sessionId: string): void => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)

    if (existing) {
      existing.sessionStartLogged = true
      existing.timestamp = Date.now()
      localStorage.setItem(key, JSON.stringify(existing))
    } else {
      const payload: PersistedSessionState = {
        currentStep: 0,
        result: null,
        resultAction: undefined,
        notSureCount: 0,
        sessionStartLogged: true,
        testType: '',
        timestamp: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(payload))
    }
  } catch (error) {
    console.warn('[sessionPersistence] Failed to mark session start:', error)
  }
}

/** Returns whether SESSION_START was already logged for this session (survives WebView reload) */
export const isSessionStartLogged = (sessionId: string): boolean => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return false
  }

  try {
    const data = readRawSession(sessionId)
    if (!data) {
      return false
    }

    if (Date.now() - data.timestamp > SESSION_TTL_MS) {
      clearSessionState(sessionId)
      return false
    }

    return data.sessionStartLogged === true
  } catch {
    return false
  }
}

/** Removes session payload (including timerStarts) for the given sessionId */
export const clearSessionState = (sessionId: string): void => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    localStorage.removeItem(buildSessionKey(sessionId))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to clear state:', error)
  }
}

/** Validates parsed localStorage JSON before restore or purge */
const isValidPersistedState = (data: unknown): data is PersistedSessionState => {
  if (!data || typeof data !== 'object') {
    return false
  }

  const d = data as Record<string, unknown>

  if (
    typeof d.currentStep !== 'number' ||
    d.currentStep < 0 ||
    d.currentStep > 6 ||
    typeof d.timestamp !== 'number' ||
    typeof d.testType !== 'string' ||
    typeof d.sessionStartLogged !== 'boolean'
  ) {
    return false
  }

  if (d.notSureCount !== undefined && typeof d.notSureCount !== 'number') {
    return false
  }

  if (d.result !== null && d.result !== undefined && typeof d.result !== 'string') {
    return false
  }

  if (d.innerSteps !== undefined) {
    if (typeof d.innerSteps !== 'object' || d.innerSteps === null || Array.isArray(d.innerSteps)) {
      return false
    }
    for (const [key, value] of Object.entries(d.innerSteps)) {
      if (typeof key !== 'string' || key.length === 0 || typeof value !== 'number') {
        return false
      }
    }
  }

  if (d.timerStarts !== undefined) {
    if (typeof d.timerStarts !== 'object' || d.timerStarts === null || Array.isArray(d.timerStarts)) {
      return false
    }
    for (const [key, value] of Object.entries(d.timerStarts)) {
      if (
        typeof key !== 'string' ||
        key.length === 0 ||
        typeof value !== 'number' ||
        !Number.isFinite(value)
      ) {
        return false
      }
    }
  }

  if (d.uploadPath !== undefined && typeof d.uploadPath !== 'string') {
    return false
  }

  if (d.imageDownloadUrls !== undefined) {
    if (!Array.isArray(d.imageDownloadUrls)) {
      return false
    }
    for (const url of d.imageDownloadUrls) {
      if (typeof url !== 'string') {
        return false
      }
    }
  }

  return true
}

/** Returns the persisted nested step for a parent step id, or undefined if missing/expired */
export const getPersistedInnerStep = (
  sessionId: string | null | undefined,
  parentStep: string
): number | undefined => {
  if (!sessionId || !isValidSessionId(sessionId) || !parentStep) {
    return undefined
  }

  const persisted = restoreSessionState(sessionId)
  const innerStep = persisted?.innerSteps?.[parentStep]
  return typeof innerStep === 'number' ? innerStep : undefined
}

/** Merges a nested step into the session payload without clobbering other session fields */
export const persistInnerStep = (
  sessionId: string | null | undefined,
  parentStep: string,
  innerStep: number
): void => {
  if (!parentStep) {
    return
  }

  if (!sessionId || !isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)

    if (existing) {
      const payload: PersistedSessionState = {
        ...existing,
        innerSteps: {
          ...(existing.innerSteps ?? {}),
          [parentStep]: innerStep,
        },
        timestamp: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(payload))
      return
    }

    const payload: PersistedSessionState = {
      currentStep: 0,
      result: null,
      resultAction: undefined,
      notSureCount: 0,
      sessionStartLogged: false,
      testType: '',
      innerSteps: { [parentStep]: innerStep },
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to persist inner step:', error)
  }
}

/** Returns the persisted timer startedAt (epoch ms) for a timer id, or undefined if missing/expired */
export const getPersistedTimerStart = (
  sessionId: string | null | undefined,
  timerId: string
): number | undefined => {
  if (!sessionId || !isValidSessionId(sessionId) || !timerId) {
    return undefined
  }

  const persisted = restoreSessionState(sessionId)
  const startedAt = persisted?.timerStarts?.[timerId]
  return typeof startedAt === 'number' && Number.isFinite(startedAt) && startedAt > 0
    ? startedAt
    : undefined
}

/** Merges a timer startedAt into the session payload without clobbering other session fields */
export const persistTimerStart = (
  sessionId: string | null | undefined,
  timerId: string,
  startedAt: number
): void => {
  if (!timerId || !Number.isFinite(startedAt) || startedAt <= 0) {
    return
  }

  if (!sessionId || !isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)

    if (existing) {
      const payload: PersistedSessionState = {
        ...existing,
        timerStarts: {
          ...(existing.timerStarts ?? {}),
          [timerId]: startedAt,
        },
        timestamp: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(payload))
      return
    }

    const payload: PersistedSessionState = {
      currentStep: 0,
      result: null,
      resultAction: undefined,
      notSureCount: 0,
      sessionStartLogged: false,
      testType: '',
      timerStarts: { [timerId]: startedAt },
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to persist timer start:', error)
  }
}

/** Returns the persisted S3 uploadPath for a session, or undefined if missing/expired */
export const getPersistedUploadPath = (
  sessionId: string | null | undefined
): string | undefined => {
  if (!sessionId || !isValidSessionId(sessionId)) {
    return undefined
  }

  const persisted = restoreSessionState(sessionId)
  const uploadPath = persisted?.uploadPath
  return typeof uploadPath === 'string' && uploadPath.length > 0 ? uploadPath : undefined
}

/** Returns persisted S3 image download URLs for a session, or undefined if missing/expired */
export const getPersistedImageDownloadUrls = (
  sessionId: string | null | undefined
): Array<string> | undefined => {
  if (!sessionId || !isValidSessionId(sessionId)) {
    return undefined
  }

  const persisted = restoreSessionState(sessionId)
  const urls = persisted?.imageDownloadUrls
  if (!Array.isArray(urls) || urls.length === 0) {
    return undefined
  }
  return urls
}

/** Merges uploadPath (and optional imageDownloadUrls) into the session payload without clobbering other fields */
export const persistUploadPath = (
  sessionId: string | null | undefined,
  uploadPath: string,
  imageDownloadUrls?: Array<string>
): void => {
  if (!uploadPath) {
    return
  }

  if (!sessionId || !isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)
    const hasNewUrls =
      Array.isArray(imageDownloadUrls) && imageDownloadUrls.length > 0

    if (existing) {
      const payload: PersistedSessionState = {
        ...existing,
        uploadPath,
        // Only overwrite URLs when a non-empty array is provided
        ...(hasNewUrls ? { imageDownloadUrls } : {}),
        timestamp: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(payload))
      return
    }

    const payload: PersistedSessionState = {
      currentStep: 0,
      result: null,
      resultAction: undefined,
      notSureCount: 0,
      sessionStartLogged: false,
      testType: '',
      uploadPath,
      ...(hasNewUrls ? { imageDownloadUrls } : {}),
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to persist upload path:', error)
  }
}

const readRawSession = (sessionId: string): PersistedSessionState | null => {
  try {
    const raw = localStorage.getItem(buildSessionKey(sessionId))
    if (!raw) {
      return null
    }

    const parsed: unknown = JSON.parse(raw)
    return isValidPersistedState(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** Called on app boot — removes expired sessions and leftover legacy timer keys */
export const purgeExpiredSessions = (): void => {
  if (!tryLocalStorage()) {
    return
  }

  try {
    const now = Date.now()
    const keysToRemove: Array<string> = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) {
        continue
      }

      if (isLegacyTimerKey(key)) {
        keysToRemove.push(key)
        continue
      }

      if (!isSessionKey(key)) {
        continue
      }

      try {
        const raw = localStorage.getItem(key)
        if (!raw) {
          keysToRemove.push(key)
          continue
        }

        const parsed: unknown = JSON.parse(raw)
        if (!isValidPersistedState(parsed)) {
          keysToRemove.push(key)
          continue
        }

        if (now - parsed.timestamp > SESSION_TTL_MS) {
          keysToRemove.push(key)
        }
      } catch {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))

    if (keysToRemove.length > 0) {
      console.log(`[sessionPersistence] Purged ${keysToRemove.length} expired session(s)`)
    }
  } catch (error) {
    console.warn('[sessionPersistence] Failed to purge expired sessions:', error)
  }
}

/**
 * Detects whether localStorage survives process kills on this device/WebView.
 * Result is reported to Sentry as storage_status tag for production monitoring.
 */
export const checkStoragePersistence = (): StorageStatus => {
  if (!tryLocalStorage()) {
    return 'disabled'
  }

  try {
    const existing = localStorage.getItem(PERSISTENCE_MARKER_KEY)
    localStorage.setItem(PERSISTENCE_MARKER_KEY, String(Date.now()))

    return existing ? 'available' : 'unverified'
  } catch {
    return 'disabled'
  }
}
